import { motion } from "framer-motion";
import { Check, Circle } from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import type { RubricItem } from "../../stores/context";

interface RubricChecklistProps {
  items: RubricItem[];
  checkedCriteria?: Set<string>;
  className?: string;
}

function RubricChecklist({ items, checkedCriteria, className }: RubricChecklistProps) {
  if (items.length === 0) {
    return (
      <p className="text-[11px] text-[var(--text-muted)] italic">
        No rubric criteria found for this assignment.
      </p>
    );
  }

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {items.map((item, i) => {
        const isChecked = checkedCriteria?.has(item.criterion) ?? false;

        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -4 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.03 }}
            className={cn(
              "flex items-start gap-2 rounded-[var(--radius-sm)] px-2 py-1.5",
              "transition-colors duration-[var(--duration-fast)]",
              isChecked ? "bg-[var(--color-signal-green)]/5" : "",
            )}
          >
            <span className="mt-0.5 shrink-0">
              {isChecked ? (
                <Check
                  size={12}
                  weight="bold"
                  className="text-[var(--color-signal-green)]"
                />
              ) : (
                <Circle
                  size={12}
                  weight="regular"
                  className="text-[var(--text-muted)]"
                />
              )}
            </span>
            <div className="flex-1 min-w-0">
              <span className={cn(
                "text-[11px] font-medium",
                isChecked ? "text-[var(--text-primary)]" : "text-[var(--text-secondary)]",
              )}>
                {item.criterion}
              </span>
              {item.weight && (
                <span className="ml-1 text-[10px] text-[var(--text-muted)]">
                  ({item.weight})
                </span>
              )}
              {item.description && (
                <p className="text-[10px] text-[var(--text-muted)] mt-0.5 leading-relaxed">
                  {item.description}
                </p>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

RubricChecklist.displayName = "RubricChecklist";

export { RubricChecklist };
export type { RubricChecklistProps };
